// What a click MEANS (plan C.2 `fast/commands.ts`, the port of
// `main.ts:828-841, 866-875, 1625-1749, 1827-1870`).
//
// This module holds the map's decisions and nothing else. It reads the
// selection, the armed pick mode and what the pointer landed on, and it
// answers with one intent on the wire. It touches no DOM node, it draws
// nothing, and it opens no listener — `input.ts` owns the events and calls
// in here, so the mouse grammar and the touch grammar cannot drift.
//
// Every collaborator arrives as a small structural interface, so a test
// hands over a plain object and pins the decision table with no engine, no
// socket and no browser. That was new ground: the client of before R505 kept
// the same code inside its boot closure, where nothing could reach it.
//
// Three decisions of the pick grammar live here and nowhere else (R469,
// R500, R501): which mark an armed order wears (`crosshairFor`), whether
// the pointer is shown at all (`setMapCursor`), and where an order point
// lands once it has snapped (`snapPoint`, `snapGather`, and the two rules
// of `snapFleetTarget`). The zoom LOD gate is here too, in `hitTest` and
// not in `hit.ts`: `hit.ts` forbids a pick that reads the cull bit, and this gate
// is the camera's scale rather than a cull bit — below it the fleet layer
// draws nothing, so a hit on a mark nobody can see would read as a broken
// map (R456, R492).

import type { Commission, PointOfInterest, Site } from '../gen/proto/aquaphobia';
import {
  FleetOp, Mission, PoiKind, ShipClass, SiteKind, TorpedoKind,
} from '../gen/proto/aquaphobia';
import type { Selection } from '../state';
import type { ControlGroups } from '../groups';
import type { UIFleet } from '../ui/overlay';
import type { TapTarget } from '../ui/touch';
import { PICK_MODES, type PickMode } from '../ui/overlay';
import {
  AIM_CONFIRM_PX, CHART, PICK_FLEET_PX, PICK_SITE_PX, SNAP_DEPOSIT_PX, SNAP_TARGET_PX,
} from '../gen/presentation';
import type { CrosshairMode } from '../render/crosshair-paths';
import { cellAt, cellCenter } from '../world';
import { laneOfMark } from '../ui/cards/lanemark';
import { main as S } from '../gen/strings';
import { pad2 } from '../locale/stamp';
import { EV_COURSE_PLOTTED } from '../audio/cues';
import { plotCursor } from './plotcursor';
import { commissionAt, commissionMarkId } from './commission-pick';
import {
  hitTest, isOwnBase, pickDeposit, tapTargetOf,
  type FleetPicker, type Hit, type SitePicker,
} from './hit';
import {
  pickVent, snapFleetTarget, snapGatherAt, snapsAt, snapWorldPoint, type FleetTarget, type SnapSurface,
} from './snap';
import {
  moveTo, newContextMission, toCell, toDeposit, toHull, type ContextMission,
} from './dragorder';

// The snap bodies moved to `snap.ts`, so the arcade match can reuse them.
// The type keeps its old door here, because its readers name this module.
export type { FleetTarget };

/**
 * The round kinds that chase a locked hull (R2018, R2779): the server
 * chases on a lock for the Remora and the Wrasse (and for the quill pod,
 * which no player fires). The catalog says which side a kind locks.
 */
const LOCK_KINDS: ReadonlySet<number> = new Set([
  TorpedoKind.TORPEDO_KIND_SEEKER, TorpedoKind.TORPEDO_KIND_WRASSE,
]);

/** How long a deploy waits for the hull the server makes for it. */
const DEPLOY_WAIT_MS = 15000;

/**
 * How near a press must land on a mark of the chart furniture, in CSS px.
 *
 * It is the fleet radius: a mark is about the size of a hull mark, and a
 * player who presses at a mark means the mark.
 */
const PICK_MARK_PX = PICK_FLEET_PX;

/** The margin of a finger round a small mark, in CSS px: the peek of a
 *  tap reads the same figure (R2851). */
const POINT_TOUCH_MARGIN_PX = CHART.pieces.wrecks.fig.touch_margin_px;

/**
 * The missions that name a place on the map. An order that carries one is a
 * plotted course, and it acknowledges itself (R547, audio.md §4.6).
 */
const PLOTTED: ReadonlySet<Mission> = new Set([
  Mission.MISSION_MOVE, Mission.MISSION_GATHER,
  Mission.MISSION_PATROL, Mission.MISSION_ATTACK_MOVE,
]);

/**
 * The mark each armed pick mode wears (R469, R500). GO… maps onto
 * 'move', because it places a destination and hunts nothing on the way,
 * exactly as the MOVE pick does.
 */
const CROSSHAIR_OF: Record<PickMode, CrosshairMode> = {
  hunting: 'attack', gathering: 'gather', escorting: 'escort',
  patrolling: 'patrol', moving: 'move', deploying: 'move',
  // SEARCH names a point of work, as a gather pick does (R2834).
  searching: 'gather',
};

/** The hull operation a context decision on a hull sends (R544). */
const HULL_OP = { escort: FleetOp.FLEET_OP_ESCORT, focus: FleetOp.FLEET_OP_FOCUS } as const;

/**
 * The one armed pick mode, or null. The modes are mutually exclusive
 * (`Overlay.armPick`), so one is armed or none is. A plain loop, because the
 * frame asks once a frame (`crosshairFor`).
 */
export function armedPick(ui: CommandUI): PickMode | null {
  for (let i = 0; i < PICK_MODES.length; i++) {
    if (ui[PICK_MODES[i]]) return PICK_MODES[i];
  }
  return null;
}

/** What the commands need of the fleet layer. */
export interface CommandFleets extends FleetPicker {
  own(): UIFleet[];
  /** Names the group every own hull wears, fleet id to slot (R403). */
  setGroups(m: ReadonlyMap<number, number>): void;
  describe(fleetId: number): FleetTarget | null;
  at(fleetId: number): { x: number; y: number } | null;
  selectedId: number;
  aimingId: number;
}

/** What the commands need of the site layer. */
export interface CommandSites extends SitePicker {
  get(siteId: number): Site | undefined;
}

/** What the commands need of the DOM chrome. `Overlay` satisfies it. */
export interface CommandUI {
  aiming: number | null;
  deploying: number;
  patrolling: number;
  hunting: number;
  escorting: number;
  /** The explicit move pick (R469). */
  moving: number;
  /** The gather pick, the Dredger's alone (R469). */
  gathering: number;
  /** The SEARCH pick of a fit hull (v2 §9.6, R2834). */
  searching: number;
  baseTorp: number;
  render(force?: boolean): void;
  hideTooltip(): void;
  closeScanReport(): void;
  /** Puts the windows away on a fleet selection (R1205). Optional, so a
   *  test double without it still selects. */
  closeTransient?(): void;
  /** Asks for one commit (R788): the confirm card beside the toasts. */
  askCommit(fleetId: number, siteId: number): void;
  /** Puts one line on screen. The map uses it for a refused commit. */
  notice(text: string, opts?: { error?: boolean } | boolean): void;
  /** The plot line of a course stands (v2 §7, R2589). Optional, so a page
   *  with no plot line answers no. */
  plotting?(): boolean;
  /** Adds a leg at a world point while the plot line stands, and answers
   *  whether the point went to the plot. */
  addPlotLeg?(x: number, y: number): boolean;
  /** True, with the error notice, when this penned hull must not sail:
   *  a hull under repair (R1883). Every deploy intent asks it first. */
  refuseDeploy(fleetId: number): boolean;
}

/** What the commands need of the socket. `Connection` satisfies it. */
export interface CommandWire {
  sendOrder(fleetId: number, xPx: number, yPx: number, mission: Mission): void;
  /** One course of several legs (v2 §7, R2576, R2589). */
  sendCourse(fleetId: number, legs: readonly { x: number; y: number; loop: boolean; passes: number }[]): void;
  /** One control-group intent: this fleet on this slot (R539). */
  sendControlGroup(slot: number, fleetId: number): void;
  /** One control-group digit recalled a group. The server counts it (R539). */
  sendControlGroupRecall(slot: number): void;
  sendFire(
    fleetId: number, xPx: number, yPx: number, lockFleetId?: number, kind?: TorpedoKind,
  ): void;
  sendCreateFleet(
    cls: ShipClass, fleetId?: number, captainId?: number, design?: number,
    target?: { xPx: number; yPx: number; mission: Mission },
  ): void;
  sendFleetAction(fleetId: number, op: FleetOp, arg?: number, load?: readonly { kind: number; count: number }[]): void;
}

/** What the commands need of the state store. `GameState` satisfies it. */
export interface CommandStore {
  readonly selection: Selection;
  /** The empire's control groups, as the last Empire frame stated them. */
  readonly groups: ControlGroups;
  readonly sites: ReadonlyMap<number, Site>;
  readonly playerId: number;
  /** The empire's faction, which tells a friendly hull from a hostile one. */
  readonly faction: number;
  select(sel: Selection): void;
  ship(cls: ShipClass): { dps: number } | undefined;
  /** The hull in the pens with this fleet id, or undefined. */
  pennedHull(fleetId: number): { cls: ShipClass } | undefined;
  /** The contest on a strategic object, when the wire has named one. */
  siegeOn(siteId: number): { siteName: string; windowHour: number } | undefined;
  /** Whether an object whose window opens at this hour takes a pledge now. */
  windowOpen(hour: number): boolean;
  /** One round of the hello catalog: `own` marks an own-side round (R2779). */
  torpedo(kind: number): { own: boolean } | undefined;
}

/**
 * What the commands need of the chart furniture (R548).
 *
 * A piece may carry a mark that takes a press — the battle marks of card 52
 * open the film of the fight they name. The furniture is asked LAST, after
 * the armed pick mode, the fleets and the sites (R702), because the live
 * map must always win a press the mark and a hull both want.
 */
export interface CommandChart {
  pick(wx: number, wy: number, tolPx: number): { id: string } | null;
}

/** What the commands need of the tooltip: the pinned peek, both ways. */
export interface CommandTooltip {
  /** Puts the tooltip up at a screen point and pins it (the touch peek). */
  pin(cx: number, cy: number): void;
  /** Takes a pinned peek away. */
  dismissPeek(): void;
  /** The cell at a screen point holds sea objects of the picture (v2
   *  §15.6, R2658): the press peeks them, and a shipping lane under the
   *  same point yields to them. A page with no sea objects leaves it out. */
  objectsAt?(cx: number, cy: number): boolean;
}

export interface CommandDeps {
  ui: () => CommandUI;
  conn: () => CommandWire;
  state: CommandStore;
  fleets: CommandFleets;
  /** The site layer is rebuilt by every hello, so it is read and not held. */
  sites: () => CommandSites | null;
  camera: { centerOn(x: number, y: number, zoom?: number): void };
  tooltip: CommandTooltip;
  /** CSS px to world px, without the shake. The point object is reused. */
  toWorld: (cx: number, cy: number, out: { x: number; y: number }) => { x: number; y: number };
  /** The camera scale, which sets the pick radii. */
  scale: () => number;
  /** The map canvas's cursor. */
  setCursor: (cursor: string) => void;
  /** The clock. A test hands over its own. */
  now?: () => number;
  /**
   * The chart furniture, or nothing in a page that draws none (R548). It is
   * read and not held, because a hello can replace the scene under it.
   */
  chart?: () => CommandChart | null;
  /**
   * The id of the point of interest whose mark holds a world point, or 0
   * (R3138). The mark is the small mark of card 69 (R2851), grown by
   * `marginPx` screen px for a finger; a nearer wreck takes the point. A
   * page that draws no points leaves it out.
   */
  pointAt?: (wx: number, wy: number, marginPx: number) => number;
  /**
   * The points of interest of the survey, or nothing in a page that knows
   * none. The vents among them are gather fields, as the deposits are
   * (R3147).
   */
  pois?: () => readonly PointOfInterest[];
  /**
   * The commissions whose pennants the marker layer draws (R2645), or
   * nothing in a page that draws none. A press on a pennant is a mark of
   * its own, `commission:<id>` (R2988).
   */
  commissions?: () => readonly Commission[];
  /**
   * A press landed on a mark of the chart furniture (R548). Phase 2's
   * battle-marks piece is what fills this in: it opens the film of the
   * fight the mark names. A page that wires nothing swallows the press.
   */
  onMark?: (id: string) => void;
  /**
   * Raises a client-local event, or nothing in a page that plays none
   * (R547). An order that names a destination plays `course_plotted`
   * through it, so the acknowledgement is the event table's to silence.
   */
  local?: (eventId: string) => void;
  /**
   * This commander sent a round selection for one hull (R3121), or
   * nothing in a page with no cue watcher. The watcher then lets the
   * change of the active round that answers the order speak.
   */
  roundOrdered?: (fleetId: number) => void;
  /**
   * The on-ramp offers of the own fleets (R3075, `laneoffer.ts`), or
   * nothing in a page that offers none. A MOVE of the context command or
   * of the MOVE pick asks for an offer; every other order of the fleet
   * takes its offer away; VIA LANE takes the offer.
   */
  laneOffer?: {
    give(fleetId: number, x: number, y: number): unknown;
    drop(fleetId: number): void;
    take(fleetId: number): readonly { x: number; y: number }[] | null;
  };
}

/** The fleet ops that give a fleet another order, so its on-ramp offer
 *  goes (R3075). A switch such as HOLD FIRE keeps it. */
const ORDER_OPS: ReadonlySet<FleetOp> = new Set([
  FleetOp.FLEET_OP_RETURN, FleetOp.FLEET_OP_DOCK, FleetOp.FLEET_OP_FOCUS,
  FleetOp.FLEET_OP_ESCORT, FleetOp.FLEET_OP_ANCHOR,
]);

export class Commands implements SnapSurface {
  /**
   * The firing solution a first tap placed, waiting for the tap that fires
   * it. Screen point and world point both: the confirm is a screen distance
   * and the shot is a world order.
   */
  private aimCandidate:
  { wx: number; wy: number; lockId: number; cx: number; cy: number } | null = null;

  /**
   * A deploy is an intent, so the fleet it makes arrives some frames later
   * with an id only the server knows (R90). These two remember that a
   * deploy is outstanding and which hulls were ours at the press.
   */
  private readonly knownFleets = new Set<number>();
  private awaitDeployUntil = 0;

  /** One reused point, so a click allocates nothing. */
  private readonly pt = { x: 0, y: 0 };

  constructor(private readonly d: CommandDeps) {}

  private now(): number {
    return this.d.now ? this.d.now() : performance.now();
  }

  /**
   * One order, and the acknowledgement that goes with it (R547).
   *
   * An order that names a DESTINATION is a plotted course, and the player
   * hears that the map took the click. A mission that carries no
   * destination — dock, defend, return — is a state change and not a
   * course, so it stays silent. Every `sendOrder` of this file goes through
   * here, so one rule covers the panel, the keys and every pick mode.
   */
  private order(fleetId: number, x: number, y: number, mission: Mission): void {
    this.d.laneOffer?.drop(fleetId);
    this.d.conn().sendOrder(fleetId, x, y, mission);
    if (PLOTTED.has(mission)) this.d.local?.(EV_COURSE_PLOTTED);
  }

  // -- the plot line (v2 §7, R2589) -----------------------------------------

  /**
   * A press on the map while the plot line stands adds a leg at that point
   * and does nothing else: it selects nothing, it gives no order and it
   * opens no mark. The left click, the tap and the right click all ask
   * here first. Answers true when the plot took the press.
   */
  plotLeg(cx: number, cy: number): boolean {
    const ui = this.d.ui();
    if (!ui.plotting?.()) return false;
    const p = this.d.toWorld(cx, cy, this.pt);
    ui.addPlotLeg?.(p.x, p.y);
    return true;
  }

  /** DONE of the plot line: the course goes on the wire, and it plays the
   *  acknowledgement of a plotted course (R547). */
  course(fleetId: number, legs: readonly { x: number; y: number; loop: boolean; passes: number }[]): void {
    if (legs.length === 0) return;
    this.d.laneOffer?.drop(fleetId);
    this.d.conn().sendCourse(fleetId, legs);
    this.d.local?.(EV_COURSE_PLOTTED);
  }

  /**
   * A transit MOVE (R3075): the order goes direct, and the desk offers the
   * lane course when a lane of the faction saves the margin. An attack, an
   * intercept, a RETURN and a commit never come here.
   */
  private transit(fleetId: number, x: number, y: number): void {
    this.order(fleetId, x, y, Mission.MISSION_MOVE);
    this.d.laneOffer?.give(fleetId, x, y);
  }

  /** VIA LANE of the on-ramp offer and shift with v (R3075): the lane
   *  course of the offer goes out as an ordinary MOVE course. It answers
   *  whether an offer stood. */
  takeLaneOffer(fleetId: number): boolean {
    const legs = this.d.laneOffer?.take(fleetId);
    if (!legs || legs.length === 0) return false;
    this.course(fleetId, legs.map((p) => ({ x: p.x, y: p.y, loop: false, passes: 0 })));
    return true;
  }

  // -- the pick --------------------------------------------------------------

  /**
   * What is under a screen point: a fleet first, then a site.
   *
   * The caller must have asked the minimap before this, because the chart
   * is a picture over the map and takes a click whole (`hit.ts`). The zoom
   * LOD gate of the fleets is in `hit.ts` `hitTest`, which the arcade match
   * reads too (R456, R501).
   */
  hitTest(cx: number, cy: number): Hit | null {
    const p = this.d.toWorld(cx, cy, this.pt);
    return this.hitWorld(p.x, p.y);
  }

  /**
   * The point of interest under a screen point, or 0 (R3138). A point is
   * no `Hit`: the hulls and the sites take a press first, and the chart
   * marks of a fight that is over take it after the point. `touch` grows
   * the mark by the margin of a finger.
   */
  pointAt(cx: number, cy: number, touch = false): number {
    const at = this.d.pointAt;
    if (!at) return 0;
    const p = this.d.toWorld(cx, cy, this.pt);
    return at(p.x, p.y, touch ? POINT_TOUCH_MARGIN_PX : 0);
  }

  /** A hit at a point the caller has already converted (R2682). */
  private hitWorld(wx: number, wy: number): Hit | null {
    return hitTest(this.d.fleets, this.d.sites(), wx, wy, this.d.scale());
  }

  // -- the order snap (R469) -------------------------------------------------

  /**
   * A world point for an order, snapped to a structure that stands near it.
   *
   * A hold, a faction installation, a control point and a deposit are things
   * a player aims a hull AT, and hitting the sprite at a low zoom is work the
   * player should not have to do. Water has no centre, so a point over water
   * never snaps and comes back as it was.
   *
   * The radius is a screen distance, so it divides by the zoom: the snap must
   * feel the same at every scale. It is wider than the R80 deposit snap,
   * because that one catches an incidental right-click and this one serves a
   * button the player pressed on purpose.
   *
   * The body is `snap.ts`, which the arcade match reads too.
   */
  snapPoint(cx: number, cy: number): { wx: number; wy: number; snapped: boolean } {
    const p = this.d.toWorld(cx, cy, this.pt);
    return snapWorldPoint(this.d.sites(), p.x, p.y, SNAP_TARGET_PX / this.d.scale());
  }

  /**
   * The hull a target pick takes, snapped. The plain hit test answers first,
   * so a click on a hull is still a click on that hull; the wider radius only
   * catches a near miss. A hull is the one legal target of these picks, so
   * this never answers with a site.
   *
   * `accept` is the rule of the one pick that asks — the escort takes an
   * own-faction hull (R784), the attack takes a hostile one (R544). One
   * body, so the two picks cannot snap differently. The body is `snap.ts`,
   * which the arcade match reads too.
   */
  private snapFleetTarget(
    cx: number, cy: number, hit: Hit | null,
    accept: (t: FleetTarget) => boolean,
  ): FleetTarget | undefined {
    const p = this.d.toWorld(cx, cy, this.pt);
    const r = SNAP_TARGET_PX / this.d.scale();
    return snapFleetTarget(this.d.fleets, hit, p.x, p.y, r, accept);
  }

  /**
   * The own-faction hull an escort pick takes, snapped (R784).
   *
   * The faction is the whole test, and the class is not part of it. The
   * server refuses an escort of another faction's hull and answers
   * "nothing to escort" (`hub/commands.go`), so a pick that offered one
   * promised an order the server would not take. `classified` was the
   * older test, and it read an enemy support hull as a friend: the server
   * states a class for that one hull alone (R198).
   */
  snapEscortTarget(cx: number, cy: number, hit: Hit | null, self: number) {
    const mine = this.d.state.faction;
    return this.snapFleetTarget(cx, cy, hit, (t) => t.faction === mine && t.id !== self);
  }

  /**
   * The hostile hull an attack pick takes, snapped (R544). A hull of another
   * faction is the one legal focus; a classified contact is still a hostile,
   * so the class it hides is not the test.
   */
  snapAttackTarget(cx: number, cy: number, hit: Hit | null) {
    return this.snapFleetTarget(cx, cy, hit, (t) => t.faction !== this.d.state.faction);
  }

  /** The cell of the gather field a gather pick would take at a screen
   *  point, a deposit or a vent, or -1 (R3147). */
  snapGather(cx: number, cy: number): number {
    const p = this.d.toWorld(cx, cy, this.pt);
    return snapGatherAt(this.d.sites(), this.d.pois?.(), p.x, p.y, SNAP_TARGET_PX / this.d.scale());
  }

  /** The cell of the vent within `px` screen px of a world point, or -1
   *  (R3147). */
  private ventAt(wx: number, wy: number, px: number): number {
    return pickVent(this.d.pois?.(), wx, wy, px / this.d.scale());
  }

  /** What a tap on a point of interest lands on (R3147): a vent is a
   *  gather field, so it is the target a deposit is; every other point
   *  selects as a site does (R3138). */
  pointTarget(id: number): TapTarget {
    const kind = this.d.pois?.().find((p) => p.id === id)?.kind;
    return kind === PoiKind.POI_KIND_VENT ? { kind: 'deposit' } : { kind: 'point' };
  }

  // -- the armed order, as a mark -------------------------------------------

  /**
   * The armed order as a crosshair mode, or null when nothing is armed
   * (R469, R500). The aim answers first: it is the one mode that can be on
   * while a fleet pick mode holds a stale id.
   */
  crosshairFor(): CrosshairMode {
    const ui = this.d.ui();
    if (ui.aiming !== null) return 'aim';
    const mode = armedPick(ui);
    return mode === null ? null : CROSSHAIR_OF[mode];
  }

  /** The fleet the escort pick is armed for (`SnapSurface`). */
  escortSelf(): number {
    return this.d.ui().escorting;
  }

  /**
   * Would this mode's pick snap at this screen point? The table is
   * `snapsAt` of `snap.ts`, which the arcade match reads too. `fast/main.ts`
   * runs it when the answer can have changed and not once a frame (§10.3).
   */
  snapAt(mode: CrosshairMode, cx: number, cy: number): boolean {
    return snapsAt(this, mode, cx, cy);
  }

  /**
   * The canvas cursor (R469). While a pick mode or the aim is armed the
   * native cursor goes away, so the drawn crosshair is the only cursor. The
   * decision lives here alone, because esc, a right-click and the CANCEL
   * press (R2164) all disarm through `cancelModes` and none of them may
   * forget it.
   *
   * `over` is what the pointer is on when no mode is armed: the hover code
   * passes 'pointer' for a hit and 'default' for open water. While the
   * plot line stands every press adds a leg, so the pointer is the teal
   * plotter mark wherever it stands (R2705).
   */
  setMapCursor(over: 'default' | 'pointer' = 'default'): void {
    if (this.crosshairFor() !== null) this.d.setCursor('none');
    else this.d.setCursor(this.d.ui().plotting?.() ? plotCursor() : over);
  }

  /**
   * The firing solution a first tap placed, in screen px, or null.
   * `fast/main.ts` draws the aim mark there, because a finger cannot hover.
   */
  get aimPoint(): { cx: number; cy: number } | null {
    return this.aimCandidate;
  }

  // -- the selection ---------------------------------------------------------

  /**
   * The ONE door every selection change goes through — esc, a click on empty
   * water, the ✕ DESELECT chip, the tap grammar and the `deselect` action
   * all arrive here.
   *
   * A probe scan report shares the selection anchor (R203) and cannot sit
   * beside a panel there, so it is dismissed by everything that dismisses a
   * selection.
   */
  select(sel: Selection): void {
    const ui = this.d.ui();
    ui.closeScanReport();
    this.d.state.select(sel);
    this.d.fleets.selectedId = sel?.kind === 'fleet' ? sel.id : 0;
    if (sel?.kind !== 'fleet' || sel.id !== ui.aiming) this.setAiming(null);
    // A fleet selection puts the windows away (R1205). A pinned peek goes
    // too, unless an aim still holds its firing-solution tooltip.
    if (sel?.kind === 'fleet') {
      ui.closeTransient?.();
      if (!ui.aiming) this.d.tooltip.dismissPeek();
    }
    ui.render(true);
  }

  /** The FLEETS panel's row: select and fly the camera to the hull. */
  selectFleet(fleetId: number): void {
    this.select({ kind: 'fleet', id: fleetId });
    const at = this.d.fleets.at(fleetId);
    if (at) this.d.camera.centerOn(at.x, at.y);
  }

  // -- the control groups (R539) ---------------------------------------------

  /**
   * Puts the selected fleet on one control group (R539).
   *
   * The gate is `ownSelection()`, the same one the order keys take: a group
   * can only ever name a fleet the player commands. The intent goes to the
   * server, which is the store, and the local model is written at once so
   * the badge and the buttons answer on the frame of the press — a quarter
   * second of nothing reads as a control that did not work (R403 §4).
   */
  assignGroup(slot: number): void {
    const sel = this.ownSelection();
    if (!sel) return;
    this.d.conn().sendControlGroup(slot, sel.id);
    if (this.d.state.groups.assign(slot, sel.id)) this.pushGroups();
  }

  /**
   * Puts one named own fleet on one control group (R1817). UNDO of a
   * replaced slot calls it, because the fleet it puts back is not the
   * selection, and it may sit in the pens (R1842). The server checks the
   * fleet and drops a hull that is not this empire's at sea or in the
   * pens, and the next Empire frame then overrules the local picture.
   */
  setGroup(slot: number, fleetId: number): void {
    if (!this.d.fleets.at(fleetId) && !this.d.state.pennedHull(fleetId)) return;
    this.d.conn().sendControlGroup(slot, fleetId);
    if (this.d.state.groups.assign(slot, fleetId)) this.pushGroups();
  }

  /**
   * Empties one control group. The wire states it as the slot with fleet
   * 0, and the local model drops the fleet at once, as `assignGroup` does.
   * An empty slot sends nothing.
   */
  releaseGroup(slot: number): void {
    const id = this.d.state.groups.fleetAt(slot);
    if (id === 0) return;
    this.d.conn().sendControlGroup(slot, 0);
    if (this.d.state.groups.release(id)) this.pushGroups();
  }

  /**
   * Recalls one control group (R539).
   *
   * The first press selects the fleet. A second press, while that fleet is
   * already selected, centres the camera on it and leaves the zoom where the
   * player put it: the two presses answer "which fleet" and "where is it",
   * and a zoom change would answer a third question nobody asked.
   *
   * An empty slot and a fleet the layer no longer holds are both silent.
   */
  recallGroup(slot: number): void {
    const id = this.d.state.groups.fleetAt(slot);
    if (id === 0) return;
    const at = this.d.fleets.at(id);
    if (!at) return;
    // The server counts the press and does nothing else with it
    // (achievements.md §3). It is sent for a slot that HELD a fleet and a
    // hull the layer still holds, because that is what a recall is.
    this.d.conn().sendControlGroupRecall(slot);
    const sel = this.d.state.selection;
    if (sel?.kind === 'fleet' && sel.id === id) this.d.camera.centerOn(at.x, at.y);
    else this.select({ kind: 'fleet', id });
  }

  /**
   * Hands the picture the store holds to the badge layer and the chrome.
   *
   * `render(true)` regenerates the chrome then and there rather than waiting
   * for the 4 Hz sweep, for the reason `assignGroup` states.
   */
  pushGroups(): void {
    this.d.fleets.setGroups(this.d.state.groups.byFleet());
    this.d.ui().render(true);
  }

  /**
   * An order that carries the fleet's own position — the panel's mission
   * buttons. The fleet layer holds where the hull is, so this is the one
   * place that reads it.
   */
  setMission(fleetId: number, mission: Mission): void {
    const at = this.d.fleets.at(fleetId);
    if (at) this.order(fleetId, at.x, at.y, mission);
  }

  /** One fleet operation — what the panel's buttons and the order keys send. */
  fleetOp(fleetId: number, op: FleetOp, arg = 0): void {
    if (ORDER_OPS.has(op)) this.d.laneOffer?.drop(fleetId);
    if (op === FleetOp.FLEET_OP_SET_ROUND) this.d.roundOrdered?.(fleetId);
    this.d.conn().sendFleetAction(fleetId, op, arg);
  }

  /** SEARCH HERE of the peek (v2 §9.1, R2629, R2649): the fleet sails to
   *  the centre of the cell and searches it. The hub judges the order. */
  searchAt(fleetId: number, cell: number): void {
    const [x, y] = cellCenter(cell);
    this.order(fleetId, x, y, Mission.MISSION_SEARCH);
  }

  /** SEARCH at a point (R2860): the fleet sails to (x, y) world px, the
   *  point of a wreck, searches there and recovers the cargo of the
   *  wrecks in reach. The hub judges the order. */
  searchPoint(fleetId: number, x: number, y: number): void {
    this.order(fleetId, x, y, Mission.MISSION_SEARCH);
  }

  /** Work a field, a deposit or a vent (R3147), and let the selection
   *  follow the order. */
  gatherAt(fleetId: number, cell: number): void {
    const [x, y] = cellCenter(cell);
    this.order(fleetId, x, y, Mission.MISSION_GATHER);
    this.select({ kind: 'fleet', id: fleetId });
  }

  // -- aim mode --------------------------------------------------------------

  /**
   * Aim mode: the next click on the water is a firing solution rather than a
   * move order. The client shows the reach and nothing more — leading the
   * contact is the player's job (game-design §8). `fleetId` 0 arms the home
   * base's Torpedo Battery; null is off.
   */
  setAiming(fleetId: number | null): void {
    const ui = this.d.ui();
    ui.aiming = fleetId;
    this.d.fleets.aimingId = fleetId ?? 0;
    // The mode decides the cursor, and the aim is a mode: the pointer goes
    // away and the drawn mark stands in for it (R469).
    this.setMapCursor();
    // Every entry and exit of the mode comes through here, so a placed touch
    // solution cannot outlive the aim it belongs to.
    this.aimCandidate = null;
    if (fleetId === null) ui.hideTooltip(); // the solution goes with the mode
    ui.render(true);
  }

  /** The FIRE button and the `f` key: arm, or disarm when already armed. */
  toggleAim(fleetId: number): void {
    this.setAiming(this.d.ui().aiming === fleetId ? null : fleetId);
  }

  // -- backing out -----------------------------------------------------------

  /**
   * Backs out of every armed pick mode, innermost first. Right-click, esc
   * and — on a touchscreen, which has neither — the CANCEL press of the armed line (R2164) all land
   * here, so the three cannot back out of different things.
   *
   * Returns true when it cleared something, so a caller can tell a cancel
   * from a command.
   */
  cancelModes(): boolean {
    const ui = this.d.ui();
    if (ui.aiming !== null) { this.setAiming(null); return true; }
    // Every pick mode at once, from the one list the overlay declares, so a
    // new mode cannot be forgotten here (R469). The mode also owned the
    // cursor, so the pointer comes back on the same statement.
    if (armedPick(ui) !== null) {
      for (const m of PICK_MODES) ui[m] = 0;
      this.setMapCursor();
      ui.render(true);
      return true;
    }
    return false;
  }

  // -- the deploy wait -------------------------------------------------------

  /**
   * Every CreateFleet intent goes through here, so the wait is armed once,
   * and a penned hull under repair is refused once (R1883): the deploy
   * press, the deploy ask and the deploy pick all end here.
   */
  createFleet(
    cls: ShipClass, fleetId = 0, captainId = 0, design = 0,
    order?: { xPx: number; yPx: number; mission: Mission },
  ): void {
    if (fleetId > 0 && this.d.ui().refuseDeploy(fleetId)) return;
    this.knownFleets.clear();
    for (const f of this.d.fleets.own()) this.knownFleets.add(f.id);
    this.awaitDeployUntil = this.now() + DEPLOY_WAIT_MS;
    this.d.conn().sendCreateFleet(cls, fleetId, captainId, design, order);
  }

  /** The load mix of a docked hull (R2017): `FLEET_OP_SET_LOAD`, and an
   *  empty mix is AUTO. */
  setLoad(fleetId: number, mix: readonly { kind: number; count: number }[]): void {
    this.d.conn().sendFleetAction(fleetId, FleetOp.FLEET_OP_SET_LOAD, 0, mix);
  }

  /**
   * Hands the selection to a fleet that has just come into being, when the
   * player asked for one (DEPLOY or GO…). The newest own id that
   * was not ours at the press is the one that was just ordered. The camera
   * is left where the player put it.
   *
   * Every state frame calls it, so a frame with no deploy outstanding
   * returns at once and reads no own list (the list allocates and sorts).
   */
  adoptNewFleets(): void {
    if (this.awaitDeployUntil === 0) return;
    let fresh = 0;
    for (const f of this.d.fleets.own()) {
      if (!this.knownFleets.has(f.id) && f.id > fresh) fresh = f.id;
    }
    if (fresh > 0) {
      this.awaitDeployUntil = 0;
      this.select({ kind: 'fleet', id: fresh });
    } else if (this.now() > this.awaitDeployUntil) {
      this.awaitDeployUntil = 0; // the order was refused, or the hull is gone
    }
  }

  // -- the context command ---------------------------------------------------

  /** The selected fleet, when it is one of ours. The tap grammar reads it too. */
  ownSelection(): UIFleet | undefined {
    const sel = this.d.state.selection;
    if (sel?.kind !== 'fleet') return undefined;
    // One lookup by id, gated on the hull's own flag: the drag preview asks
    // on every finger move (R1208), and the own list allocates and sorts.
    const f = this.d.fleets.describe(sel.id);
    return f?.own ? f : undefined;
  }

  /** Does a hull of this class carry guns? The DPS gate of R79. */
  private armed(cls: ShipClass): boolean {
    return (this.d.state.ship(cls)?.dps ?? 0) > 0;
  }

  /** The site record, from the store first and the layer second. */
  private siteOf(id: number): Site | undefined {
    return this.d.state.sites.get(id) ?? this.d.sites()?.get(id);
  }

  /**
   * The context command (v5 grammar, R544): hostile fleet → focus, friendly
   * fleet → escort, deposit + Dredger → gather, own base → return home,
   * strategic object + armed hull → commit, anywhere else → move. It acts on
   * the selected own fleet and does nothing without one.
   *
   * The mouse reaches it on right-click, a finger on a tap the grammar reads
   * as an order (`ui/touch.ts`) and on the release of a drag order (R1208),
   * so the three can never drift. The decision itself is `contextMission`,
   * which the drag preview reads too: the line a finger sees is the order
   * this sends.
   */
  issueContextCommand(cx: number, cy: number): void {
    const mine = this.ownSelection();
    if (!mine) return; // nothing selected: right-click does nothing
    const m = this.contextMission(cx, cy, this.mission, mine);
    if (!m) return;
    switch (m.kind) {
      case 'gather': this.gatherAt(mine.id, m.cell); return;
      case 'escort':
      case 'focus': this.fleetOp(mine.id, HULL_OP[m.kind], m.targetId); return;
      case 'return': this.fleetOp(mine.id, FleetOp.FLEET_OP_RETURN); return;
      case 'commit': {
        // The window is the only time an object takes a pledge (R787), so a
        // shut window is refused here rather than at the server, where the
        // answer would arrive after the card.
        if (m.open) { this.d.ui().askCommit(mine.id, m.targetId); return; }
        const g = this.d.state.siegeOn(m.targetId);
        if (g) {
          this.d.ui().notice(S.commit_window_shut({
            place: g.siteName, hour: pad2(g.windowHour),
          }), true);
        }
        return;
      }
      case 'move': this.transit(mine.id, m.wx, m.wy);
    }
  }

  /** The decision `issueContextCommand` reuses, so a click allocates nothing. */
  private readonly mission = newContextMission();

  /**
   * What the context command would do at a screen point, for one own fleet
   * (R1208). It sends nothing: `issueContextCommand` acts on the answer, and
   * the drag preview draws it.
   *
   * `out` is the caller's and is filled in place. `mine` defaults to the
   * selected own fleet; the answer is null when there is none.
   *
   * The snap is the right-click's own and no wider: a hull within the fleet
   * pick radius, a site within the site pick radius, and for a Dredger a
   * deposit or a vent within the R80 deposit snap, and a vent within the
   * site pick radius as a hit (R3147). That keeps the right-click exactly
   * as it was, and it is the "light snap" the drag order asks for.
   */
  contextMission(
    cx: number, cy: number, out: ContextMission, mine = this.ownSelection(),
  ): ContextMission | null {
    if (!mine) return null;
    const p = this.d.toWorld(cx, cy, this.pt);
    const wx = p.x, wy = p.y;
    const hit = this.hitWorld(wx, wy);
    moveTo(out, wx, wy);
    // Deposit snap (R80): when the selected fleet is a hauler — the class
    // whose right-click on a deposit means gather — a click near a field
    // resolves to the field before any fleet can steal the pick. A vent is
    // a field as a deposit is, at the same radius (R3147).
    const dredger = mine.cls === ShipClass.SHIP_CLASS_DREDGER;
    if (dredger) {
      const dep = pickDeposit(this.d.sites(), wx, wy, this.d.scale());
      if (dep) return toDeposit(out, dep.cell);
      const vent = this.ventAt(wx, wy, SNAP_DEPOSIT_PX);
      if (vent >= 0) return toDeposit(out, vent);
    }
    if (hit?.kind === 'fleet' && hit.id !== mine.id) {
      // A friendly hull under the right button is an escort (R544). Own
      // hulls count as friendly; the selected hull itself is not a target,
      // so it falls through to the move below.
      if (hit.faction === this.d.state.faction) return toHull(out, 'escort', hit.id, this.d.fleets.at(hit.id));
      // A named hostile is a focus: the fleet chases that hull and fires at
      // it alone (R544). A gunless hull can only sail there, which is the DPS
      // gate of R79. The server re-validates both.
      if (this.armed(mine.cls)) return toHull(out, 'focus', hit.id, this.d.fleets.at(hit.id));
      return out;
    }
    // A vent within the pick radius of a site is a hit on a field, as a
    // deposit under the press is (R3147). A hull or a site takes the press
    // first.
    if (!hit && dredger) {
      const vent = this.ventAt(wx, wy, PICK_SITE_PX);
      if (vent >= 0) return toDeposit(out, vent);
    }
    if (hit?.kind === 'site') {
      const site = this.siteOf(hit.id);
      if (site?.kind === SiteKind.SITE_KIND_DEPOSIT && dredger) {
        return toDeposit(out, site.cell);
      }
      if (site && isOwnBase(site, this.d.state.playerId)) return toCell(out, 'return', site.cell);
      // A strategic object under the right button is a COMMIT (R788). The
      // order sails the fleet to the object and pledges it on arrival, so
      // it spends the fleet for the whole window: the card asks first.
      //
      // A support hull never fights at an object (R448) and a gunless one
      // has nothing to bring, so the DPS gate of R79 stands here too and
      // such a hull falls through to the move below.
      if (site?.kind === SiteKind.SITE_KIND_CONTROL_POINT && this.armed(mine.cls)) {
        const g = this.d.state.siegeOn(site.id);
        if (g) {
          toCell(out, 'commit', site.cell);
          out.targetId = site.id;
          out.open = this.d.state.windowOpen(g.windowHour);
          return out;
        }
      }
    }
    return out;
  }

  /**
   * The own fleet a drag order may start on at a screen point, or 0
   * (R1208). A press starts no drag while the aim or a pick mode is armed:
   * the next press belongs to that mode, and an escort pick takes an own
   * hull.
   */
  dragFleetAt(cx: number, cy: number): number {
    const ui = this.d.ui();
    // A finger on a hull adds a leg while the plot line stands (R2589).
    if (ui.aiming !== null || armedPick(ui) !== null || ui.plotting?.()) return 0;
    const hit = this.hitTest(cx, cy);
    if (hit?.kind !== 'fleet' || !hit.own) return 0;
    return this.d.fleets.describe(hit.id)?.own ? hit.id : 0;
  }

  // -- the armed pick modes --------------------------------------------------

  /**
   * The armed target-pick modes — deploy destination, patrol lane, attack-
   * move point, move destination, gather field, escort target. One body for
   * both grammars: a mouse click reaches it and so does a tap. Returns true
   * when a mode took the point.
   *
   * Aiming is deliberately not here. It fires on one click but on two taps
   * (a fingertip covers the water it is aiming at), so the two callers keep
   * their own aim branch.
   */
  runPickMode(cx: number, cy: number, hit: Hit | null): boolean {
    const ui = this.d.ui();
    const mode = armedPick(ui);
    if (mode === null) return false;
    this.pick(mode, ui[mode], cx, cy, hit);
    ui[mode] = 0; // a press the mode does not take cancels the pick
    ui.render(true);
    return true;
  }

  /** What one armed pick mode does with the press. `fleetId` is the armed fleet. */
  private pick(mode: PickMode, fleetId: number, cx: number, cy: number, hit: Hit | null): void {
    switch (mode) {
      case 'deploying': {
        // A fresh fleet sails to wherever is clicked instead of idling at the
        // base — the same hit-test grammar as a right-click order.
        // The destination snaps like a MOVE pick does (R469): GO…
        // places a destination, and both orders mean the same thing.
        const p = this.snapPoint(cx, cy);
        const hull = this.d.state.pennedHull(fleetId)?.cls;
        let mission = Mission.MISSION_MOVE;
        let tx = p.wx, ty = p.wy;
        if (hit?.kind === 'site') {
          const site = this.siteOf(hit.id);
          if (site?.kind === SiteKind.SITE_KIND_DEPOSIT
            && hull === ShipClass.SHIP_CLASS_DREDGER) {
            mission = Mission.MISSION_GATHER;
            [tx, ty] = cellCenter(site.cell);
          }
        } else if (!hit && hull === ShipClass.SHIP_CLASS_DREDGER) {
          // A vent under the press is a field, as a deposit is (R3147).
          const w = this.d.toWorld(cx, cy, this.pt);
          const vent = this.ventAt(w.x, w.y, PICK_SITE_PX);
          if (vent >= 0) {
            mission = Mission.MISSION_GATHER;
            [tx, ty] = cellCenter(vent);
          }
        }
        this.createFleet(0, fleetId, 0, 0, { xPx: tx, yPx: ty, mission });
        return;
      }
      case 'patrolling': {
        // Patrol lane pick: the click sets the far end — the lane runs from
        // where the fleet is now to here, and it shuttles (R40/R41).
        const p = this.d.toWorld(cx, cy, this.pt);
        this.order(fleetId, p.x, p.y, Mission.MISSION_PATROL);
        return;
      }
      case 'hunting': {
        // The attack pick (R79, R544). A press on a hostile contact, or a near
        // miss beside one, names that contact: the fleet chases that hull and
        // fires at it alone. A press on open water keeps the attack-move — the
        // fleet advances on the point and hunts what it hears on the way. The
        // point takes no structure snap: a hold near the point is not the order.
        const t = this.snapAttackTarget(cx, cy, hit);
        if (t) { this.fleetOp(fleetId, FleetOp.FLEET_OP_FOCUS, t.id); return; }
        const p = this.d.toWorld(cx, cy, this.pt);
        this.order(fleetId, p.x, p.y, Mission.MISSION_ATTACK_MOVE);
        return;
      }
      case 'moving': {
        // The explicit move (R469). MISSION_MOVE never leaves course, so the
        // fleet sails past a contact it hears instead of turning on it. A
        // right-click cannot ask for this: on an enemy fleet the context
        // command sends a focus. The point snaps to a structure, so
        // "sail to that hold" costs one press and one click.
        const p = this.snapPoint(cx, cy);
        this.transit(fleetId, p.wx, p.wy);
        return;
      }
      case 'gathering': {
        // Gather field pick (R469). The mission has one legal target, a
        // field, so the pick snaps to the nearest deposit or vent (R3147)
        // and cancels on anything else — the shape the escort pick below
        // already has.
        const cell = this.snapGather(cx, cy);
        if (cell >= 0) this.gatherAt(fleetId, cell);
        return;
      }
      case 'searching': {
        // The SEARCH pick of the ⋯ (v2 §9.6, R2834): the press names the
        // cell, and the fleet sails to its centre and searches it. The hub
        // judges the order, rock and the hull class too.
        const p = this.d.toWorld(cx, cy, this.pt);
        const cell = cellAt(p.x, p.y);
        if (cell >= 0) this.searchAt(fleetId, cell);
        return;
      }
      case 'escorting': {
        // Escort target pick: an own-faction fleet confirms, and a near miss
        // snaps to the hull (R469).
        const t = this.snapEscortTarget(cx, cy, hit, fleetId);
        if (t) this.fleetOp(fleetId, FleetOp.FLEET_OP_ESCORT, t.id);
      }
    }
  }

  // -- the mouse's own branches ---------------------------------------------

  /**
   * A shot by mouse: the aim point comes from the click, and the lock from a
   * contact the player clicked while carrying seekers.
   */
  fireAt(cx: number, cy: number, hit: Hit | null): void {
    const aimingId = this.d.ui().aiming;
    if (aimingId === null) return;
    const p = this.d.toWorld(cx, cy, this.pt);
    this.sendShot(aimingId, p.x, p.y, this.lockFor(aimingId, hit));
    this.setAiming(null);
  }

  /**
   * The hull a shot locks, or 0 (R2783). The round is the firing kind of an
   * own hull, or the hold's pick for the hold (id 0). A round that chases
   * on a lock (`LOCK_KINDS`) locks the clicked hull of the side its effect
   * lands on: a hostile contact for a hostile round (the Remora), an
   * own-faction hull for an own-side round (the Wrasse, catalog `own`).
   * The server holds the same rule and refuses any other lock.
   */
  private lockFor(aimingId: number, hit: Hit | null): number {
    if (hit?.kind !== 'fleet') return 0;
    let kind = 0;
    if (aimingId === 0) kind = this.d.ui().baseTorp;
    else {
      const shooter = this.d.fleets.describe(aimingId);
      if (shooter?.own === true) kind = shooter.firingKind;
    }
    if (!LOCK_KINDS.has(kind)) return 0;
    const own = this.d.state.torpedo(kind)?.own === true;
    const sameSide = hit.faction === this.d.state.faction;
    return own === sameSide ? hit.id : 0;
  }

  /** One shot on the wire. A base shot (id 0) names its round — the panel's pill pick (R86). */
  private sendShot(aimingId: number, wx: number, wy: number, lock: number): void {
    this.d.conn().sendFire(aimingId, wx, wy, lock, aimingId === 0 ? this.d.ui().baseTorp : 0);
  }

  /** The left click on the map, once the minimap has refused it. */
  clickMap(cx: number, cy: number): void {
    if (this.plotLeg(cx, cy)) return;
    const ui = this.d.ui();
    const hit = this.hitTest(cx, cy);
    if (ui.aiming !== null) { this.fireAt(cx, cy, hit); return; }
    // RTS grammar (v5): left selects and deselects, right commands.
    if (this.runPickMode(cx, cy, hit)) return;
    if (hit) { this.select({ kind: hit.kind, id: hit.id }); return; }
    // A point of interest selects as a site does (R3138), so its card
    // stands in the context slot with its order presses.
    const point = this.pointAt(cx, cy);
    if (point) { this.select({ kind: 'point', id: point }); return; }
    // The chart furniture is asked LAST (R702, and no longer R548): a mark
    // is a picture of a fight that is over, and the map under it is the
    // game. An armed pick mode, a hull and a site all take the click first,
    // so a battle mark never eats an order and never blocks a selection.
    // The press opens the film only on water nothing else wants.
    if (this.pressMark(cx, cy)) return;
    this.select(null); // left in empty water deselects
  }

  /**
   * Opens the film of a mark under a screen point. Answers true when a mark
   * took the press.
   *
   * The mouse and the finger both end here, so the last-place rule of R702
   * is written once: the caller asks this only after the aim, the armed
   * pick mode, the hulls, the sites and the orders had the press.
   */
  pressMark(cx: number, cy: number): boolean {
    const mark = this.markAt(cx, cy);
    if (!mark) return false;
    this.d.onMark?.(mark.id);
    return true;
  }

  /**
   * The mark of the chart furniture under a screen point, or null.
   *
   * The tolerance scales with the zoom, so the target keeps its size on
   * SCREEN, which is the rule every other pick here keeps.
   */
  markAt(cx: number, cy: number): { id: string } | null {
    const p = this.d.toWorld(cx, cy, this.pt);
    const tol = PICK_MARK_PX / this.d.scale();
    // A commission pennant stands over the chart furniture, so it takes
    // the press first: the press opens its card (R2988).
    const com = commissionAt(this.d.commissions?.() ?? [], p.x, p.y, tol);
    if (com) return { id: commissionMarkId(com.id) };
    const chart = this.d.chart?.();
    if (!chart) return null;
    const hit = chart.pick(p.x, p.y, tol);
    // A shipping lane is a pick of the chart (R2658), and sea objects lie
    // on lanes: the sea objects of the cell take the press first.
    if (hit && laneOfMark(hit.id) && this.d.tooltip.objectsAt?.(cx, cy)) return null;
    return hit;
  }

  // -- the touch grammar -----------------------------------------------------

  /** What a tap landed on, in the vocabulary `ui/touch.ts` decides with. */
  tapTarget(hit: Hit | null): TapTarget {
    const site = hit?.kind === 'site' ? this.siteOf(hit.id) : undefined;
    return tapTargetOf(hit, site, this.d.state.playerId);
  }

  /**
   * Aiming by finger is two steps (the mouse still fires on one click): the
   * first tap places the solution and pins the firing-solution tooltip, a
   * second tap on that same point looses the round, and a tap anywhere else
   * moves the solution. A fingertip hides the water underneath it, so
   * "point and shoot" would be shooting blind.
   */
  tapAim(cx: number, cy: number): void {
    const aimingId = this.d.ui().aiming;
    if (aimingId === null) return;
    const placed = this.aimCandidate;
    if (placed && Math.hypot(cx - placed.cx, cy - placed.cy) <= AIM_CONFIRM_PX) {
      // The confirm, sent exactly as the mouse sends it.
      this.sendShot(aimingId, placed.wx, placed.wy, placed.lockId);
      this.d.tooltip.dismissPeek();
      this.setAiming(null);
      return;
    }
    const p = this.d.toWorld(cx, cy, this.pt);
    const wx = p.x, wy = p.y;
    const lock = this.lockFor(aimingId, this.hitWorld(wx, wy));
    this.aimCandidate = { wx, wy, lockId: lock, cx, cy };
    // The tooltip draws the firing solution for this point while the aim is
    // armed; pinning it keeps the reach on screen with no finger on it.
    this.d.tooltip.pin(cx, cy);
  }
}
